import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    inject,
    isDevMode,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { catchError, firstValueFrom, map, of, take, tap } from 'rxjs';
import { authInterceptor } from './core/auth/auth.interceptor';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { UserSettingsService } from './core/user/user-data/user-settings/user-settings.service';
import { apiUrlInterceptorFn } from './interceptors/api.interceptor';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(
            withInterceptors([apiUrlInterceptorFn, authInterceptor])
        ),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
            // withDebugTracing()
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'fr',
                        label: 'Français',
                    },
                ],
                defaultLang: 'fr',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const userSettingsService = inject(UserSettingsService);
                const defaultLang = translocoService.getDefaultLang();

                return () =>
                    userSettingsService.get().pipe(
                        take(1),
                        tap((userSettings) => {
                            if (userSettings && userSettings.language) {
                                translocoService.setActiveLang(
                                    userSettings.language
                                );
                            } else {
                                translocoService.setActiveLang(defaultLang);
                            }
                        }),
                        catchError(() => {
                            const localLanguage =
                                localStorage.getItem('activeLang');

                            if (localLanguage) {
                                translocoService.setActiveLang(localLanguage);
                            } else {
                                translocoService.setActiveLang(defaultLang);
                            }
                            return of();
                        }),
                        map(() =>
                            firstValueFrom(translocoService.load(defaultLang))
                        )
                    );
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-goodCircleMint',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-goodCircleMint',
                        name: 'GoodCircleMint',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
function provideQuillConfig(arg0: {
    modules: { syntax: boolean; toolbar: any[] };
}):
    | import('@angular/core').Provider
    | import('@angular/core').EnvironmentProviders {
    throw new Error('Function not implemented.');
}
